import type { JSX } from 'react'
import { T } from '../tokens.js'
import { formatKw, grouped } from './Pill.js'

/**
 * The hourly profile chart — the signature surface, and the enforceable form of
 * **display rule 11: the window is hourly, not aggregate.**
 *
 * *(Task 14's plan text says "display rule 7" here. Rule 7 is about note
 * content; the rule this component exists for is rule 11, "resist any layout
 * that reduces the 5–10pm profile to a single figure". The brief that
 * commissioned this task carries the correction. Recorded rather than silently
 * fixed, because the plan's wording is what a later reader will find.)*
 *
 * Every hour the server sends is drawn as its own mark: a tick, a point on the
 * series, a dot, a value label and an hour label. There is no aggregate here,
 * no average line and no "window total", and adding one is a spec question
 * rather than a chart option.
 *
 * **One component, three surfaces, one `variant` prop.** It is used by the
 * drill-down, by presentation mode and by the one-pager. A forked customer copy
 * is two charts that drift, and the one that drifts into showing something
 * internal is always the customer-facing one. `variant` changes exactly two
 * strings and nothing else; everything a customer must not see is absent from
 * the props, not hidden by a branch.
 *
 * **The binding hour carries four independent signals** — the drop line, the
 * larger dot, the red value label, and the caption underneath. That redundancy
 * is the design, not an accident: one of the four is a colour a colour-blind
 * rep cannot see, and this is the number they commit to a customer.
 *
 * ## What this component is *given*, and what it must not invent
 *
 * **The API returns five hours (17–21).** The mock draws thirteen (12pm–12am)
 * with the out-of-window hours dashed "for context only", and no route serves
 * them. This component renders whatever profile it is handed and dashes
 * anything outside `eventWindow`; it does not fabricate context hours. With
 * today's payload nothing is dashed, so the footnote about dashed hours is not
 * drawn either — a legend for a mark that is not on the page sends a rep
 * looking for data that was never sent.
 *
 * **`eventWindow` is a prop because no route carries it.** The drill-down and
 * presentation payloads both send `profile` and `nomination` and neither sends
 * the configured window, and there is no configuration route (the same gap that
 * leaves `sizeBandEdgesKw` unreachable). The caller supplies it. The chart
 * title is *derived* from it rather than written out, so a window that is not
 * 5–10pm cannot end up drawn one way and labelled another.
 *
 * ## The binding hour, and why the dropped band is on the *left*
 *
 * `curtailment.ts` anchors the examined span at the window **close**
 * (`theoreticalSpanStart = endHour - spanLength`), so a shorter event starts
 * *later* and drops the **earliest** hours — which on a decaying afternoon
 * profile are the highest ones. The mock's own `bindIndex` scans forward from
 * the window start and is backwards; where the mock and the engine disagree the
 * engine is authoritative. Desert Ridge binds at 21:00 for durations 2, 3, 4
 * and 5, and its dropped band covers 5pm, not 9pm.
 *
 * That band is drawn from `nomination.spanStartHour` / `spanEndHour` rather
 * than from the `scenario` flag, which means **it appears on the default view
 * too**: the configured event duration is 4 hours and the window is 5, so
 * 5:00pm is outside the examined span before anyone touches a what-if control.
 * That is true — the brief's own verified figures give Desert Ridge a span of
 * 18:00–22:00 against a 17:00–22:00 window — and hiding it would make the chart
 * claim that all five hours produced the nomination when only four did.
 */

/** One hour of the profile. The engine's `HourValue`, retyped: this package has
 *  no reference to the engine or the server project, on purpose. */
export interface HourValue {
  hour: number
  kw: number
}

/**
 * The part of the engine's `Nomination` this chart draws.
 *
 * `flags` is deliberately absent — it is internal advisory signal that R-28
 * strips from customer payloads entirely, and a component used on a customer
 * screen should not be able to receive it. `spanEndHour` is **exclusive**, as
 * `curtailment.ts` reports it (`max(hour) + 1`).
 */
export interface ChartNomination {
  kw: number
  bindingHour: number
  spanStartHour: number
  spanEndHour: number
  /** True when `kw` was entered by hand rather than read off the profile. */
  overridden: boolean
}

export interface EventWindow {
  startHour: number
  /** Exclusive, matching `config.eventWindow` and the engine's own reading. */
  endHour: number
}

export interface ProfileChartProps {
  profile: readonly HourValue[]
  nomination: ChartNomination
  eventWindow: EventWindow
  /**
   * Required, with no default. A default would mean that *forgetting* the prop
   * on the presentation screen renders internal copy to a customer, and the
   * safe direction for a mistake here is a type error.
   */
  variant: 'internal' | 'customer'
  /** Display rule 2: a scenario is visibly a scenario, everywhere. */
  scenario?: boolean
}

// The mock's geometry, exact. `viewBox="0 0 800 308"`, points on a track from
// x=56 to x=728, the x-axis running 16px further to 744, and a 206px plot band
// from y=34 to y=240 whose top is `max(profile) * 1.16`.
const VIEW_WIDTH = 800
const VIEW_HEIGHT = 308
const PLOT_LEFT = 56
const AXIS_RIGHT = 744
const TRACK_RIGHT = 728
const TRACK_WIDTH = TRACK_RIGHT - PLOT_LEFT
const PLOT_TOP = 34
const PLOT_BOTTOM = 240
const BAND_HEIGHT = PLOT_BOTTOM - PLOT_TOP
const EDGE_TOP = 26
const HEADROOM = 1.16
const HOUR_LABEL_Y = 264
const CAPTION_Y = 288
const FOOTNOTE_Y = 304

/** The one string in here that is a legend for a mark, so it is drawn only when
 *  that mark is. */
export const CHART_FOOTNOTE = 'DASHED = OUTSIDE THE EVENT WINDOW, SHOWN FOR CONTEXT ONLY'

/** Nothing to draw. Neutral wording on purpose — display rule 4: absence is not
 *  deficiency, and this is not a slot somebody is being asked to fill. */
export const NO_PROFILE_LABEL = 'NO HOURLY PROFILE TO SHOW'

/** `21` as `9PM`, `12` as `12PM`, `24` as `12AM`. */
function clock(hour: number): string {
  const h = ((Math.trunc(hour) % 24) + 24) % 24
  const twelve = h % 12 === 0 ? 12 : h % 12
  return `${twelve}${h < 12 ? 'AM' : 'PM'}`
}

/** The hour without its meridiem: `21` as `9`. */
function bare(hour: number): string {
  const h = ((Math.trunc(hour) % 24) + 24) % 24
  return String(h % 12 === 0 ? 12 : h % 12)
}

/**
 * `EVENT WINDOW 5:00–10:00PM — ONLY THESE HOURS COUNT`, derived.
 *
 * The mock writes this out as a literal and the plan mandates it verbatim; it
 * is computed here so that a differently configured window cannot be drawn one
 * way and titled another. Against the seeded window (17–22) it reproduces the
 * mandated sentence character for character, which `ProfileChart.test.tsx`
 * pins as a literal rather than by importing this function.
 */
function titleFor(window: EventWindow): string {
  const start = `${bare(window.startHour)}:00`
  const end = `${bare(window.endHour)}:00${clock(window.endHour).replace(/^\d+/, '')}`
  return `EVENT WINDOW ${start}–${end} — ONLY THESE HOURS COUNT`
}

interface Point {
  hour: number
  kw: number
  x: number
  y: number
  inWindow: boolean
}

/**
 * Runs of consecutive points, split where window membership changes.
 *
 * Each out-of-window run is extended by the neighbouring point at either end so
 * the dashed and solid polylines meet rather than leaving a gap at the window
 * edge — the same overlap the mock gets from `seg(0,5)` and `seg(10,12)` both
 * including the boundary index.
 */
function runsOf(points: readonly Point[]): { inside: Point[][]; outside: Point[][] } {
  const inside: Point[][] = []
  const outside: Point[][] = []
  let i = 0
  while (i < points.length) {
    const start = i
    const flag = points[start]?.inWindow === true
    while (i < points.length && (points[i]?.inWindow === true) === flag) i += 1
    const run = points.slice(start, i)
    if (flag) {
      inside.push(run)
    } else {
      const before = points[start - 1]
      const after = points[i]
      outside.push([
        ...(before === undefined ? [] : [before]),
        ...run,
        ...(after === undefined ? [] : [after]),
      ])
    }
  }
  return { inside, outside }
}

function polyline(run: readonly Point[]): string {
  return run.map((p) => `${p.x},${p.y.toFixed(1)}`).join(' ')
}

export function ProfileChart({
  profile,
  nomination,
  eventWindow,
  variant,
  scenario = false,
}: ProfileChartProps): JSX.Element {
  const svgStyle = {
    display: 'block',
    width: '100%',
    height: 'auto',
    fontFamily: T.fontFamily,
  } as const
  const title = titleFor(eventWindow)

  // Sorted defensively. The wire sends the hours in order, but a scrambled
  // array would draw a polyline that zig-zags across the plot and still look
  // like a chart, which is the worst kind of wrong.
  const hours = [...profile].sort((a, b) => a.hour - b.hour)

  if (hours.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        style={svgStyle}
        role="img"
        aria-label={NO_PROFILE_LABEL}
        data-profile-chart=""
        data-variant={variant}
        data-empty=""
      >
        <text x={PLOT_LEFT} y={18} fontSize={11} fontWeight={T.weight.bold} letterSpacing="0.06em" fill={T.color.navy} data-chart-title="">
          {title}
        </text>
        <text
          x={(PLOT_LEFT + AXIS_RIGHT) / 2}
          y={(PLOT_TOP + PLOT_BOTTOM) / 2}
          textAnchor="middle"
          fontSize={T.micro.size}
          letterSpacing={T.micro.tracking}
          fill={T.color.micro}
        >
          {NO_PROFILE_LABEL}
        </text>
      </svg>
    )
  }

  const kws = hours.map((h) => h.kw)
  const peak = Math.max(...kws)
  // A flat-zero profile would divide by zero and draw NaN coordinates, which
  // render as nothing at all rather than as an error.
  const max = peak > 0 ? peak * HEADROOM : 1
  const y = (kw: number): number => PLOT_BOTTOM - (kw / max) * BAND_HEIGHT

  const first = hours[0]?.hour ?? 0
  const last = hours[hours.length - 1]?.hour ?? first
  // Points are laid out by *index*, so a gap in the hours does not open a gap
  // in the drawing. With the mock's own thirteen contiguous hours this is
  // exactly `X(i) = 56 + i * 56`, and `ProfileChart.test.tsx` pins that.
  const step = hours.length > 1 ? TRACK_WIDTH / (hours.length - 1) : 0
  const xOfIndex = (i: number): number =>
    hours.length > 1 ? PLOT_LEFT + i * step : PLOT_LEFT + TRACK_WIDTH / 2
  // Bands and window rules can fall on an hour that has no data point (the
  // window close, 10pm, is never in the payload), so they are placed by
  // extrapolating the same spacing and clamped to the plot.
  const perHour = last > first ? TRACK_WIDTH / (last - first) : 0
  const xOfHour = (hour: number): number =>
    Math.min(AXIS_RIGHT, Math.max(PLOT_LEFT, PLOT_LEFT + (hour - first) * perHour))

  const inWindow = (hour: number): boolean =>
    hour >= eventWindow.startHour && hour < eventWindow.endHour

  const points: Point[] = hours.map((h, i) => ({
    hour: h.hour,
    kw: h.kw,
    x: xOfIndex(i),
    y: y(h.kw),
    inWindow: inWindow(h.hour),
  }))
  const { inside, outside } = runsOf(points)
  const anyDashed = outside.length > 0

  const bandLeft = xOfHour(eventWindow.startHour)
  const bandRight = xOfHour(eventWindow.endHour)

  // The hours inside the window that the examined span did not reach. On this
  // engine that is always a leading run — the span is anchored at the close —
  // but the trailing case is drawn too rather than assumed away.
  const dropped: { key: string; from: number; to: number }[] = []
  if (nomination.spanStartHour > eventWindow.startHour) {
    dropped.push({ key: 'lead', from: bandLeft, to: xOfHour(nomination.spanStartHour) })
  }
  if (nomination.spanEndHour < eventWindow.endHour) {
    dropped.push({ key: 'trail', from: xOfHour(nomination.spanEndHour), to: bandRight })
  }
  const spanLength = nomination.spanEndHour - nomination.spanStartHour
  const droppedLabel = `NOT IN THE ${spanLength}-HOUR EVENT`

  const binding = points.find((p) => p.hour === nomination.bindingHour)
  const seriesStroke = scenario ? T.color.red : T.color.navy
  const nominationY = y(nomination.kw)
  const nominationPrefix = variant === 'customer' ? 'YOUR COMMITMENT' : 'NOMINATION'
  // "BINDS" is a claim about where the number came from. A hand-entered
  // nomination did not come from any hour, so only the weakest-hour wording —
  // which stays true either way — is said in that case.
  const captionSuffix =
    variant === 'customer' || nomination.overridden ? ' — WEAKEST HOUR' : ' BINDS'

  /**
   * The caption is anchored away from whichever edge it is near.
   *
   * Measured, not assumed: with today's five-hour payload the binding hour is
   * the *last* point, at x=728, and a middle-anchored `9PM — WEAKEST HOUR`
   * runs off the right of the 800-unit viewBox and is clipped — on the
   * customer-facing variant, whose caption is the longer of the two. Anchoring
   * to the axis end instead keeps it inside at any font, and the drop line
   * directly above it still says which hour it belongs to.
   */
  const nearRight = binding !== undefined && binding.x >= AXIS_RIGHT - 60
  const nearLeft = binding !== undefined && binding.x <= PLOT_LEFT + 60
  const captionAnchor = nearRight ? 'end' : nearLeft ? 'start' : 'middle'
  const captionX = nearRight ? AXIS_RIGHT : nearLeft ? PLOT_LEFT : (binding?.x ?? PLOT_LEFT)

  const summary =
    `${title}. ${nominationPrefix} ${formatKw(nomination.kw)}. ` +
    `${clock(nomination.bindingHour)}${captionSuffix.toLowerCase()}. ` +
    `Hour by hour: ${points.map((p) => `${clock(p.hour)} ${formatKw(p.kw)}`).join(', ')}.`

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      style={svgStyle}
      role="img"
      aria-label={summary}
      data-profile-chart=""
      data-variant={variant}
      data-scenario={scenario ? 'true' : 'false'}
    >
      {/* 1. The title. */}
      <text
        x={PLOT_LEFT}
        y={18}
        fontSize={11}
        fontWeight={T.weight.bold}
        letterSpacing="0.06em"
        fill={T.color.navy}
        data-chart-title=""
      >
        {title}
      </text>

      {/* 2. The event-window band. */}
      <rect
        x={bandLeft}
        y={PLOT_TOP}
        width={Math.max(0, bandRight - bandLeft)}
        height={BAND_HEIGHT}
        fill={T.tint.chartWindow}
        data-window-band=""
      />

      {/* 3. The hours the examined span did not reach. */}
      {dropped.map((d) => (
        <rect
          key={d.key}
          x={d.from}
          y={PLOT_TOP}
          width={Math.max(0, d.to - d.from)}
          height={BAND_HEIGHT}
          fill={T.tint.chartDropped}
          data-dropped-band={d.key}
        />
      ))}
      {dropped
        .filter((d) => d.to - d.from >= 150)
        .map((d) => (
          <text
            key={`${d.key}-label`}
            x={(d.from + d.to) / 2}
            y={PLOT_BOTTOM - 10}
            textAnchor="middle"
            fontSize={T.micro.size}
            fontWeight={T.weight.bold}
            letterSpacing="0.05em"
            fill={T.color.micro}
            data-dropped-label=""
          >
            {droppedLabel}
          </text>
        ))}

      {/* 4. One tick per hour, tinted by whether that hour counts. */}
      {points.map((p) => (
        <line
          key={`tick-${p.hour}`}
          x1={p.x}
          y1={PLOT_TOP}
          x2={p.x}
          y2={PLOT_BOTTOM}
          stroke={p.inWindow ? T.color.chartTickIn : T.color.chartTickOut}
          strokeWidth={1}
          data-hour-tick=""
          data-hour={p.hour}
          data-in-window={p.inWindow ? 'true' : 'false'}
        />
      ))}

      {/* 5. The window edges. */}
      <line x1={bandLeft} y1={EDGE_TOP} x2={bandLeft} y2={PLOT_BOTTOM} stroke={T.color.navy} strokeWidth={1.5} data-window-edge="start" />
      <line x1={bandRight} y1={EDGE_TOP} x2={bandRight} y2={PLOT_BOTTOM} stroke={T.color.navy} strokeWidth={1.5} data-window-edge="end" />

      {/* 6. The axes. */}
      <line x1={PLOT_LEFT} y1={PLOT_BOTTOM} x2={AXIS_RIGHT} y2={PLOT_BOTTOM} stroke={T.color.border} strokeWidth={1.5} data-axis="x" />
      <line x1={PLOT_LEFT} y1={EDGE_TOP} x2={PLOT_LEFT} y2={PLOT_BOTTOM} stroke={T.color.border} strokeWidth={1.5} data-axis="y" />

      {/* 7. Anything outside the window, dashed and grey. */}
      {outside.map((run, i) => (
        <polyline
          key={`out-${i}`}
          points={polyline(run)}
          fill="none"
          stroke={T.color.outOfWindow}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          data-series="out-of-window"
        />
      ))}

      {/* 8. The window itself — red whenever a scenario is active. */}
      {inside.map((run, i) => (
        <polyline
          key={`in-${i}`}
          points={polyline(run)}
          fill="none"
          stroke={seriesStroke}
          strokeWidth={3}
          data-series="in-window"
        />
      ))}

      {/* 9. The nomination threshold. */}
      <line
        x1={bandLeft}
        y1={nominationY}
        x2={bandRight}
        y2={nominationY}
        stroke={T.color.red}
        strokeWidth={1.5}
        strokeDasharray="5 4"
        data-nomination-line=""
      />
      <text
        x={bandLeft + 8}
        y={nominationY + 17}
        fontSize={11}
        fontWeight={T.weight.bold}
        letterSpacing="0.04em"
        fill={T.color.red}
        data-nomination-label=""
      >
        {`${nominationPrefix} ${formatKw(nomination.kw)}`}
      </text>

      {/* 10. The binding hour's drop line — signal one of four. */}
      {binding === undefined ? null : (
        <line
          x1={binding.x}
          y1={binding.y}
          x2={binding.x}
          y2={PLOT_BOTTOM}
          stroke={T.color.red}
          strokeWidth={2}
          data-binding-dropline=""
        />
      )}

      {/* 11. Every hour a dot and a value; the binding hour bigger and red —
              signals two and three. */}
      {points.map((p) => (
        <circle
          key={`dot-${p.hour}`}
          cx={p.x}
          cy={p.y}
          r={3.5}
          fill={T.color.navy}
          data-hour-dot=""
          data-hour={p.hour}
          data-kw={p.kw}
        />
      ))}
      {binding === undefined ? null : (
        <circle cx={binding.x} cy={binding.y} r={5.5} fill={T.color.red} data-binding-dot="" data-hour={binding.hour} />
      )}
      {points.map((p) => {
        const isBinding = p.hour === nomination.bindingHour
        return (
          <text
            key={`value-${p.hour}`}
            x={p.x}
            y={p.y - 13}
            textAnchor="middle"
            fontSize={12}
            fontWeight={T.weight.semi}
            fill={isBinding ? T.color.red : T.color.ink}
            data-hour-value=""
            data-hour={p.hour}
            data-binding-label={isBinding ? '' : undefined}
          >
            {grouped(p.kw, 1)}
          </text>
        )
      })}

      {/* 12. Hour labels; the window edges bold and navy. */}
      {points.map((p, i) => {
        const edge = p.hour === eventWindow.startHour || p.hour === eventWindow.endHour
        const named = edge || i === 0 || i === points.length - 1
        return (
          <text
            key={`hour-${p.hour}`}
            x={p.x}
            y={HOUR_LABEL_Y}
            textAnchor="middle"
            fontSize={T.micro.size}
            fontWeight={edge ? T.weight.bold : T.weight.body}
            fill={edge ? T.color.navy : T.color.micro}
            data-hour-label=""
            data-hour={p.hour}
          >
            {named ? clock(p.hour) : bare(p.hour)}
          </text>
        )
      })}

      {/* 13. The caption — signal four. */}
      {binding === undefined ? null : (
        <text
          x={captionX}
          y={CAPTION_Y}
          textAnchor={captionAnchor}
          fontSize={12}
          fontWeight={T.weight.bold}
          letterSpacing="0.04em"
          fill={T.color.red}
          data-binding-caption=""
        >
          {`${clock(binding.hour)}${captionSuffix}`}
        </text>
      )}

      {/* 14. The footnote, only when there is a dashed hour for it to explain. */}
      {anyDashed ? (
        <text
          x={PLOT_LEFT}
          y={FOOTNOTE_Y}
          fontSize={T.micro.size}
          letterSpacing="0.05em"
          fill={T.color.chartFootnote}
          data-footnote=""
        >
          {CHART_FOOTNOTE}
        </text>
      ) : null}
    </svg>
  )
}
